import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Preview Mode Indicator
 * Shows a banner when sensitive data blurring preview mode is active
 */
export const PreviewModeIndicator: React.FC = () => {
  const { translate } = useTranslation();
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  useEffect(() => {
    const checkPreviewMode = () => {
      // Check URL parameter first (for initial load)
      const urlParams = new URLSearchParams(window.location.search);
      const hasPreviewParam = urlParams.get('preview') === 'true';

      // Check sessionStorage (tab-specific storage)
      const previewMode = sessionStorage.getItem('sensitive-data-preview-mode');

      if (hasPreviewParam && !previewMode) {
        // Set sessionStorage if URL has preview param but storage doesn't
        sessionStorage.setItem('sensitive-data-preview-mode', 'true');
        setIsPreviewMode(true);
      } else {
        setIsPreviewMode(previewMode === 'true');
      }
    };

    // Check on mount
    checkPreviewMode();

    // Also check periodically for same-tab changes
    const interval = setInterval(checkPreviewMode, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleExitPreview = () => {
    sessionStorage.removeItem('sensitive-data-preview-mode');
    setIsPreviewMode(false);

    // Remove preview parameter from URL and reload
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    window.history.replaceState({}, '', url.toString());
    window.location.reload();
  };

  if (!isPreviewMode) {
    return null;
  }

  return (
    <div className="fixed top-14 left-0 right-0 z-[9998] h-14 bg-yellow-500 dark:bg-yellow-600 border-b-2 border-yellow-600 dark:border-yellow-700 shadow-lg">
      <div className="container mx-auto h-full px-4">
        <div className="flex items-center justify-between gap-4 h-full">
          <div className="flex items-center gap-2 text-yellow-900 dark:text-yellow-100 flex-1 min-w-0">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="font-semibold text-sm sm:text-base truncate">
              <span className="uppercase tracking-wide">
                {translate('pos.previewMode.label', 'PREVIEW MODE')}:
              </span>{' '}
              {translate(
                'pos.previewMode.message',
                'Sensitive data blurring is active. This is a preview window, not the real dashboard.'
              )}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExitPreview}
            className="flex-shrink-0 bg-yellow-600 hover:bg-yellow-700 text-yellow-100 border-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800 dark:text-yellow-100 dark:border-yellow-800"
          >
            <X className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">
              {translate('pos.previewMode.exitPreview', 'Exit Preview')}
            </span>
            <span className="sm:hidden">
              {translate('pos.previewMode.exit', 'Exit')}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
